"use client";

import type { FC } from "react";

import type { HouseRecord } from "../../../types";
import { Flex } from "../../Flex/Flex";
import { Icon, type IconProps } from "../../Icon/Icon";
import { Text, type TextProps } from "../../Text/Text";
import { ValueParser } from "../../ValueParser/ValueParser";
import {
  EnergyLabel,
  type EnergyLabelProps,
} from "../../EnergyLabel/EnergyLabel";

export type HouseSummaryProps = {
  record: HouseRecord;
  color?: TextProps["color"];
  iconSize?: IconProps["size"];
  textVariant?: TextProps["variant"];
};

/**
 * HouseSummary is a component that displays an object card.
 * It is a wrapper around the ExpandableCard component.
 * It is used to display an object (house, apartment, etc.) card in a carousel.
 */
export const HouseSummary: FC<HouseSummaryProps> = ({
  record,
  color,
  iconSize = "s",
  textVariant,
}) => (
  <>
    {/* <pre>{JSON.stringify(record, null, 2)}</pre> */}

    {!!record.energyLabel &&
      !["niet verplicht", "onbekend"].includes(
        record.energyLabel.toLowerCase(),
      ) && (
        <EnergyLabel
          label={record.energyLabel as EnergyLabelProps["label"]}
          size="s"
          style={{ alignSelf: "flex-start" }}
        />
      )}

    {!!record.livingAreaSize && record.livingAreaSize > 0 && (
      <Flex gap="xs" align="center">
        <Icon icon="objectLivingAreaSize" color={color} size={iconSize} />
        <Text color={color} variant={textVariant}>
          <b>
            <ValueParser
              value={{
                type: "squareMeter",
                value: record.livingAreaSize,
              }}
            />
          </b>{" "}
          wonen
        </Text>
      </Flex>
    )}

    {!!record.plotSize && `${record.plotSize}` !== "0" && (
      <Flex gap="xs" align="center">
        <Icon icon="objectPlotSize" color={color} size={iconSize} />
        <Text color={color} variant={textVariant}>
          <b>
            <ValueParser
              value={{
                type: "squareMeter",
                value: record.plotSize,
              }}
            />
          </b>{" "}
          perceel
        </Text>
      </Flex>
    )}

    {!!record.numberOfBedrooms && record.numberOfBedrooms > 0 && (
      <Flex gap="xs" align="center">
        <Icon icon="doors" color={color} size={iconSize} />
        <Text color={color} variant={textVariant}>
          <b>{record.numberOfBedrooms}</b> kamer
          {record.numberOfBedrooms > 1 && record.numberOfBedrooms !== 0
            ? "s"
            : ""}
        </Text>
      </Flex>
    )}
  </>
);
